import { UNIT_TRANSLATE } from 'constants/translate'
import { EKPIUnit } from 'enums/kpi'
import { TChartLayout } from 'models/common'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  LabelList,
  Legend,
  LegendProps,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts'
import { translateValueMetric } from 'utils/common'
import NumberUtil from 'utils/number'
import { useTooltipContext } from '../BaseTooltip/CustomTooltip'
import { CustomizedAxisTick } from './components/CustomAxisTick'
import { CustomLabel } from './components/CustomLable'
import { renderSingleLegend } from './components/SingleChartLegend'

export interface RechartsProps {
  key: string
  yAxisId?: string
  color: string
  display: boolean
  isHover: boolean
}

interface BaseSignleChartProps<T> {
  data: T[]
  layout: TChartLayout
  colors: any
  chartProps: Array<RechartsProps>
  yAxisProps?: Array<{
    yAxisId: string
    orientation: 'left' | 'right'
    unit: string
  }>
  width?: string | number
  height?: string | number
  w?: number
  unit?: string
  margin?: {
    top?: number
    bottom?: number
    right?: number
    left?: number
  }
  legendProps?: Pick<
    LegendProps,
    'height' | 'width' | 'layout' | 'align' | 'margin' | 'formatter' | 'iconType' | 'iconSize' | 'onClick'
  >
}

function BaseSignleChart<T>({
  data,
  layout,
  colors,
  width = '100%',
  height = '100%',
  unit,
  w,
  chartProps,
  yAxisProps,
  ...props
}: BaseSignleChartProps<T>) {
  const { openTooltip, closeTooltip } = useTooltipContext()

  const tooltipContent = (e: any) => {
    return (
      <>
        <span>{translateValueMetric(e.name)}: </span>
        <span>{e && NumberUtil.format(e.value.toFixed(2), { unit: unit as EKPIUnit })}</span>
      </>
    )
  }

  return (
    <ResponsiveContainer width={width} height={height}>
      <ComposedChart
        data={data}
        {...props}
        margin={{
          left: 10,
          right: layout === 'horizontal' ? 35 : 0,
          top: layout === 'horizontal' ? 10 : 30,
          bottom: w && w < 10 ? 30 : 0
        }}
        layout={layout === 'horizontal' ? 'vertical' : 'horizontal'}
        className={layout === 'vertical' ? 'chart-horizontal' : 'chart-vertical'}
      >
        <CartesianGrid strokeDasharray='3 3' />
        {layout === 'horizontal' && (
          <>
            <XAxis type='number'>
              <Label
                value={unit && unit !== EKPIUnit.POINT ? `(${UNIT_TRANSLATE[unit as EKPIUnit]})` : ''}
                offset={-10}
                dx={25}
                dy={-23}
                position={'insideBottomRight'}
              />
            </XAxis>
            <YAxis dataKey='groupName' type='category' tickLine={true} tick={<CustomizedAxisTick />} />
          </>
        )}
        {layout === 'vertical' && (
          <>
            <ReferenceLine
              x={(data as any)?.[0]?.groupName}
              label={(e) =>
                CustomLabel({
                  ...e,
                  title: unit && unit !== EKPIUnit.POINT ? `(${UNIT_TRANSLATE[unit as EKPIUnit]})` : '',
                  dy: 20,
                  dx: 30
                })
              }
            />
            <XAxis dataKey='groupName' />
            {yAxisProps ? (
              yAxisProps.map((cp) => {
                return <YAxis key={cp.yAxisId} yAxisId={cp.yAxisId} orientation={cp.orientation} unit={unit} />
              })
            ) : (
              <YAxis />
            )}
          </>
        )}
        <Legend content={renderSingleLegend} />
        {chartProps.map((cp) => {
          let color
          if (cp.key.includes('actualPerform')) {
            color = colors?.['actualPerform']
          }
          if (cp.key.includes('baseline')) {
            color = colors?.['baseline']
          }
          if (cp.key.includes('target')) {
            color = colors?.['target']
          }

          return (
            <Bar
              radius={layout === 'vertical' ? [4, 4, 0, 0] : [0, 4, 4, 0]}
              key={cp.key}
              yAxisId={cp?.yAxisId}
              dataKey={cp.key}
              barSize={22}
              fill={color}
              hide={!cp.display}
              onMouseEnter={(e) => {
                return openTooltip({
                  content: tooltipContent(e.tooltipPayload[0])
                })
              }}
              onMouseLeave={() => closeTooltip()}
            >
              <LabelList
                dataKey={cp.key}
                position={layout === 'vertical' ? 'top' : 'right'}
                className='font-semibold text-[0.5rem]'
                formatter={(v: number) =>
                  v !== undefined ? NumberUtil.format(v.toFixed(2), { unit: unit as EKPIUnit }) : ''
                }
              />
            </Bar>
          )
        })}
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export default BaseSignleChart
